import React from 'react';
import { graphql } from '../../../../generated';
import { useSuspenseQuery } from '../../../../util/use_suspense_query';
import { Card } from '../../../../components/Card/Card';
import { t } from '../../../../locales/translate';
import { withSuspendedCard } from '../../../../util/suspense';
import { Icon } from '../../../../components/Icon/Icon';
import { useConfirmation } from '../../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { useMutation } from '@apollo/client';

const GET_USER_LOGIN_BARCODE = graphql(`
  query GetUserLoginBarcode($id: ID!) {
    user(id: $id) {
      id
      loginBarcode
      loginBarcodeImage {
        originalUrl
      }
      permissions {
        id
        showLoginBarcode
        updateLoginBarcode
      }
    }
  }
`);

const GENERATE_LOGIN_BARCODE = graphql(`
  mutation GenerateLoginBarcode($id: ID!) {
    regenerateLoginBarcode(input: { userId: $id }) {
      user {
        id
        loginBarcode
        loginBarcodeImage {
          originalUrl
        }
      }
    }
  }
`);

const REMOVE_LOGIN_BARCODE = graphql(`
  mutation RemoveLoginBarcode($id: ID!) {
    removeLoginBarcode(input: { userId: $id }) {
      user {
        id
        loginBarcode
        loginBarcodeImage {
          originalUrl
        }
      }
    }
  }
`);

type Props = {
  id: string;
};

function UserLoginBarcode$({ id }: Props): JSX.Element {
  const { data } = useSuspenseQuery(GET_USER_LOGIN_BARCODE, { variables: { id } });
  const [generateBarcode] = useMutation(GENERATE_LOGIN_BARCODE);
  const [removeBarcode] = useMutation(REMOVE_LOGIN_BARCODE);

  const { confirm: confirmGenerate } = useConfirmation({
    title: t.component.userLoginBarcode.generate.title,
    message: t.component.userLoginBarcode.generate.areYouSure,
    action: async () => await generateBarcode({ variables: { id } }),
  });

  const { confirm: confirmRemove } = useConfirmation({
    title: t.component.userLoginBarcode.remove.title,
    message: t.component.userLoginBarcode.remove.areYouSure,
    action: async () => await removeBarcode({ variables: { id } }),
  });

  return (
    <Card
      title={t.component.userLoginBarcode.title}
      actions={
        <>
          {data.user.loginBarcodeImage && (
            <a href={data.user.loginBarcodeImage.originalUrl} download title={t.component.userLoginBarcode.download}>
              <Icon className="product-image__icon" name="download--sm" />
            </a>
          )}
          {data.user.permissions.updateLoginBarcode && <Icon name="plus" onClick={confirmGenerate} />}
          {data.user.loginBarcodeImage && data.user.permissions.updateLoginBarcode && (
            <Icon name="trash" onClick={confirmRemove}></Icon>
          )}
        </>
      }
    >
      {data.user.permissions.showLoginBarcode
        ? data.user.loginBarcode
          ? t.component.userLoginBarcode.barcodeExists
          : t.component.userLoginBarcode.noBarcode
        : t.component.userLoginBarcode.noPermission}
    </Card>
  );
}

export const UserLoginBarcode = withSuspendedCard(UserLoginBarcode$, {
  loadingHeight: 400,
  loadingTitle: t.component.userLoginBarcode.title,
});
